/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-06-18 08:26:02
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { useState } from 'react';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Link, Button, Icon, Badge, Popover, ActionList } from '@shopify/polaris';
import { routes } from 'src/routes';
import { DuplicateMinor, MobileHorizontalDotsMajor } from '@shopify/polaris-icons';

import history from 'src/routes/history';
import {
  COLUMN_PREDICTIVE_BUNDLE,
  PLACEMENT_BUNDLE_OPTIONS,
  BUNDLE_STATUS_OPTIONS
} from 'src/constants/bundle';
import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';

type Props = {
  key: number,
  widget: Object,
  handleWidgetAction: ({ widgetId: string, widgetName: string }, type) => void
};

type ComposedProps = Props & WithI18nProps;

const WidgetRow = (props: ComposedProps) => {
  const { widget, handleWidgetAction, i18n } = props;

  const [popoverAction, setPopoverAction] = useState(false);

  // Copy clip board
  const copyClipBoard = (copyText: string) => {
    try {
      const inputText = document.createElement('input');
      inputText.value = copyText;
      document.body.appendChild(inputText);
      inputText.select();

      document.execCommand('copy');
      document.body.removeChild(inputText);
      showSABToast(i18n.translate('AdminHTML.Common.Action.copied', { t: copyText }), false);
    } catch (error) {}
  };

  // Recommendation widget id
  const renderWidgetId = (item, index) => {
    const widgetId = _.get(item, 'id', '');
    return (
      <td
        className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--widgetId"
        key={index}>
        {widgetId}
        <span className="copy">
          <Button icon={DuplicateMinor} plain onClick={() => copyClipBoard(widgetId)} />
        </span>
      </td>
    );
  };

  // Recommendation widget name
  const renderWidgetName = (item, index) => {
    const widgetName = _.get(item, 'widgetName', '');
    return (
      <td
        className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--name"
        key={index}>
        <Link
          onClick={() => {
            history.push(routes.predictiveBundle.view(item.id));
          }}
          removeUnderline>
          {widgetName}
        </Link>
      </td>
    );
  };

  // Recommendation widget recommendation type
  const renderPlacement = (item, index) => {
    const placement = _.get(item, 'placement', []);
    return (
      <td
        className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--type"
        key={index}>
        {placement.map(e => {
          const optionValue = PLACEMENT_BUNDLE_OPTIONS.find(option => option.value === e);
          const label = optionValue ? optionValue.label : PLACEMENT_BUNDLE_OPTIONS[0].label;
          return (
            <p style={{ margin: '2px' }}>
              <Badge>{i18n.translate(label)}</Badge>
            </p>
          );
        })}
      </td>
    );
  };

  // Recommendation widget status
  const renderStatus = (item, index) => {
    const statusWidget = _.get(item, 'bundleStatus', 'inactive');
    let status = 'default';
    if (statusWidget === 'active') {
      status = 'info';
    }
    const statusOption = BUNDLE_STATUS_OPTIONS.find(option => option.value === statusWidget);
    const label = statusOption ? statusOption.label : BUNDLE_STATUS_OPTIONS[0].label;
    return (
      <td
        className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--status"
        key={index}>
        <Badge status={status}>{i18n.translate(label)}</Badge>
      </td>
    );
  };

  // Handle edit widget
  const handleEdit = widgetId => {
    history.push(routes.predictiveBundle.view(widgetId));
  };

  // Recommendation widget actions
  const renderActions = (item, index) => {
    const widgetId = _.get(item, 'id', '');
    const widgetName = _.get(item, 'widgetName', '');
    const widgetStatus = _.get(item, 'bundleStatus', '');
    const activator = (
      <span className="group_btn">
        <Button onClick={() => setPopoverAction(true)} plain>
          <Icon color="base" source={MobileHorizontalDotsMajor} />
        </Button>
      </span>
    );

    let menuActions = [
      {
        content: i18n.translate('AdminHTML.PredictiveBundle.Common.pause'),
        id: 'pause',
        onAction: () => {
          handleWidgetAction({ widgetId, widgetName }, 'inactive');
          setPopoverAction(false);
        }
      },
      {
        content: i18n.translate('AdminHTML.PredictiveBundle.Common.activate'),
        id: 'active',
        onAction: () => {
          handleWidgetAction({ widgetId, widgetName }, 'active');
          setPopoverAction(false);
        }
      },
      {
        content: i18n.translate('AdminHTML.PredictiveBundle.Common.edit'),
        id: 'edit',
        onAction: () => handleEdit(widgetId)
      },
      {
        content: i18n.translate('AdminHTML.PredictiveBundle.Common.delete'),
        id: 'delete',
        onAction: () => {
          handleWidgetAction({ widgetId, widgetName }, 'delete');
          setPopoverAction(false);
        }
      }
    ];
    if (widgetStatus === 'active') {
      _.remove(menuActions, e => e.id === 'active');
    } else {
      _.remove(menuActions, e => e.id === 'pause');
    }
    return (
      <td
        className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--actions"
        key={index}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Popover
            active={popoverAction}
            activator={activator}
            onClose={() => setPopoverAction(false)}>
            <Popover.Pane>
              <ActionList items={menuActions} />
            </Popover.Pane>
          </Popover>
        </div>
      </td>
    );
  };

  /* #region: Render content components */
  const renderContent = (item, column, index) => {
    switch (column) {
      case 'name':
        return renderWidgetName(item, index);
      case 'status':
        return renderStatus(item, index);
      case 'widgetId':
        return renderWidgetId(item, index);
      case 'placement':
        return renderPlacement(item, index);
      case 'actions':
        return renderActions(item, index);
      default:
        return '';
    }
  };

  return (
    <tr>
      {_.map(COLUMN_PREDICTIVE_BUNDLE, (column, index) => renderContent(widget, column.value, index))}
    </tr>
  );
};

export default withI18n()(WidgetRow);
